// File: client/src/components/MainMenu.js
//
// The menu is driven entirely by the variant registry — it renders one card per
// registered variant and never hardcodes a specific mode. Selecting a variant opens
// a setup step (opponent, difficulty, time control) with a rulebook preview.

import React, { useState } from 'react';
import {
  User, LogIn, LogOut, Users, Bot, History, Globe, Settings, Clock, X, Play, BookOpen,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import LoginModal from './LoginModal';
import SettingsModal from './SettingsModal';
import RulebookModal from '../platform/RulebookModal';
import { listVariants } from '../platform/variantRegistry';

// Opponent options, filtered per variant.supportedGameTypes.
const OPPONENTS = [
  { id: 'ai', label: 'vs Computer', icon: Bot },
  { id: 'local', label: 'vs Friend (Local)', icon: Users },
  { id: 'online', label: 'Online', icon: Globe },
];
const DIFFICULTIES = ['easy', 'medium', 'hard'];

// Setup modal: choose opponent (+ difficulty for AI) + time control, with a rulebook
// preview. Emits { variantId, opponent, difficulty, timeControl }.
const GameSetupModal = ({ variant, user, onStart, onClose }) => {
  const supported = OPPONENTS.filter(o => variant.supportedGameTypes.includes(o.id));
  const [opponent, setOpponent] = useState(supported[0]?.id || 'ai');
  const [difficulty, setDifficulty] = useState('medium');
  const [timeControl, setTimeControl] = useState(10);
  const [showRules, setShowRules] = useState(false);

  const timeOptions = [
    { value: 5, label: '5 min', desc: 'Blitz' },
    { value: 10, label: '10 min', desc: 'Rapid' },
    { value: 15, label: '15 min', desc: 'Standard' },
  ];

  const onlineNeedsLogin = opponent === 'online' && !user;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-xl p-6 w-full max-w-sm shadow-2xl border border-slate-700">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <span>{variant.icon}</span> {variant.name}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition">
            <X size={20} />
          </button>
        </div>

        <p className="text-slate-400 text-sm mb-1">{variant.tagline}</p>
        <button
          onClick={() => setShowRules(true)}
          className="text-xs text-fuchsia-300 hover:text-fuchsia-200 flex items-center gap-1 mb-4"
        >
          <BookOpen size={13} /> How to Play
        </button>

        {/* Opponent */}
        <div className="mb-4">
          <p className="text-slate-300 text-sm font-medium mb-2">Opponent</p>
          <div className="grid grid-cols-1 gap-2">
            {supported.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setOpponent(id)}
                className={`py-2 px-3 rounded-lg font-medium transition flex items-center gap-2 border-2 ${
                  opponent === id
                    ? 'bg-blue-600 border-blue-400 text-white'
                    : 'bg-slate-700 border-slate-600 text-slate-300 hover:border-slate-500'
                }`}
              >
                <Icon size={16} /> {label}
              </button>
            ))}
          </div>
        </div>

        {/* Difficulty (AI only) */}
        {opponent === 'ai' && (
          <div className="mb-4">
            <p className="text-slate-300 text-sm font-medium mb-2">Difficulty</p>
            <div className="grid grid-cols-3 gap-2">
              {DIFFICULTIES.map(d => (
                <button
                  key={d}
                  onClick={() => setDifficulty(d)}
                  className={`py-2 rounded-lg font-medium capitalize transition border-2 ${
                    difficulty === d
                      ? 'bg-blue-600 border-blue-400 text-white'
                      : 'bg-slate-700 border-slate-600 text-slate-300 hover:border-slate-500'
                  }`}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Time control */}
        <div className="mb-6">
          <p className="text-slate-300 text-sm font-medium mb-2 flex items-center gap-2">
            <Clock size={16} /> Time Control
          </p>
          <div className="grid grid-cols-3 gap-2">
            {timeOptions.map(({ value, label, desc }) => (
              <button
                key={value}
                onClick={() => setTimeControl(value)}
                className={`py-2 px-2 rounded-lg font-medium transition flex flex-col items-center border-2 ${
                  timeControl === value
                    ? 'bg-blue-600 border-blue-400 text-white'
                    : 'bg-slate-700 border-slate-600 text-slate-300 hover:border-slate-500'
                }`}
              >
                <span className="text-sm font-bold">{label}</span>
                <span className="text-[10px] opacity-75">{desc}</span>
              </button>
            ))}
          </div>
        </div>

        <button
          disabled={onlineNeedsLogin}
          onClick={() =>
            onStart({
              variantId: variant.id,
              opponent,
              difficulty: opponent === 'ai' ? difficulty : null,
              timeControl,
            })
          }
          className="w-full py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg font-medium hover:opacity-90 transition flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Play size={20} /> {onlineNeedsLogin ? 'Login required for Online' : 'Start Game'}
        </button>
      </div>

      {showRules && (
        <RulebookModal
          variantName={variant.name}
          rulebook={variant.rulebook}
          onClose={() => setShowRules(false)}
        />
      )}
    </div>
  );
};

const MainMenu = ({ onStartGame, onViewHistory }) => {
  const { user, logout, loading } = useAuth();
  const [showLogin, setShowLogin] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState(null);

  const variants = listVariants();

  const handleStartGame = (config) => {
    onStartGame(config);
    setSelectedVariant(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4">
      {/* Title */}
      <div className="text-center mb-8">
        <h1 className="text-4xl sm:text-5xl font-bold text-white mb-2">♔ Chess Variants</h1>
        <p className="text-slate-400">Choose your game mode</p>
      </div>

      <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 w-full max-w-md space-y-4">
        {/* User section */}
        {user ? (
          <div className="flex items-center justify-between p-3 bg-white/10 rounded-lg">
            <div className="flex items-center gap-2 text-white">
              <User size={20} />
              <div>
                <p className="font-medium">{user.username}</p>
                <p className="text-xs text-slate-400">
                  ELO: {user.eloRating || 1200} • Games: {user.gamesPlayed || 0}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowSettings(true)} className="text-slate-300 hover:text-white p-1 transition" title="Settings">
                <Settings size={20} />
              </button>
              <button onClick={logout} className="text-slate-300 hover:text-white transition" title="Logout">
                <LogOut size={20} />
              </button>
            </div>
          </div>
        ) : (
          <div className="flex gap-2">
            <button
              onClick={() => setShowLogin(true)}
              className="flex-1 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition flex items-center justify-center gap-2"
            >
              <LogIn size={20} /> Login
            </button>
            <button onClick={() => setShowSettings(true)} className="py-3 px-4 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition" title="Settings">
              <Settings size={20} />
            </button>
          </div>
        )}

        {/* Game modes — one card per registered variant */}
        <div className="space-y-2">
          <p className="text-slate-300 text-sm font-medium">Game Modes</p>
          {variants.map(variant => (
            <button
              key={variant.id}
              onClick={() => setSelectedVariant(variant)}
              className={`w-full p-3 rounded-lg font-medium transition flex items-center gap-3 text-left bg-gradient-to-r ${variant.accent} hover:opacity-90 text-white`}
            >
              <span className="text-2xl">{variant.icon}</span>
              <span className="flex-1">
                <span className="block font-bold">{variant.name}</span>
                <span className="block text-xs opacity-80">{variant.tagline}</span>
              </span>
            </button>
          ))}
        </div>

        {/* Game history */}
        {user && (
          <button
            onClick={onViewHistory}
            className="w-full py-3 bg-slate-600 text-white rounded-lg font-medium hover:bg-slate-700 transition flex items-center justify-center gap-2"
          >
            <History size={20} /> Game History
          </button>
        )}
      </div>

      <p className="mt-6 text-slate-500 text-xs">Chess Variants Platform</p>

      {/* Modals */}
      {showLogin && <LoginModal onClose={() => setShowLogin(false)} />}
      {showSettings && <SettingsModal onClose={() => setShowSettings(false)} />}
      {selectedVariant && (
        <GameSetupModal
          variant={selectedVariant}
          user={user}
          onStart={handleStartGame}
          onClose={() => setSelectedVariant(null)}
        />
      )}
    </div>
  );
};

export default MainMenu;
